import React, {useState, useEffect, useLayoutEffect} from 'react';
import {SafeAreaView, View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  FormTextInput,
  RadioButton,
  Pressable,
  FormNumberInput,
  Text,
  LinearProgress,
  CloseButton,
  PrimaryButton,
  Grouping,
  Link,
} from '~/components/controls';
import {PublicContentView} from '~/components/shared';
import {KvkSearchDialog} from '~/components/shared/Dialogs';
import {
  addJobIsLease,
  addJobIsFleet,
  addJobLessor,
  addJobSetCarRepairTypeId,
  addJobSetLicense,
  addJobSetMileage,
  addJobSetCompanyCar,
  addJobSetCompany,
} from '~/actions';
import {useDispatch, useSelector} from '~/lib/hooks';
import {car as carApi} from '~/api/public';

import DirectionsCarIcon from '~/images/md-icons/directions_car/materialicons/24px.svg';
import PinIcon from '~/images/md-icons/pin/materialicons/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import StoreIcon from '~/images/md-icons/store/materialicons/24px.svg';
import ShoppingCartIcon from '~/images/md-icons/shopping_cart/materialicons/24px.svg';

const Index = ({navigation, route, onDismiss}) => {
  const dispatch = useDispatch();
  const dealer_id = route?.params?.dealer_id;
  const is_change = route?.params?.is_change;

  const stored = useSelector((state) => ({
    is_company_car: state.is_company_car,
    is_lease: state.is_lease,
    is_fleet: state.is_fleet,
    lessor: state.lessor,
    license: state.license,
    mileage: state.mileage,
    repair_type_id: state.repair_type_id,
    company: state.company,
  }));

  const [is_company_car, setIsCompanyCar] = useState(stored.is_company_car);
  const [company, setCompany] = useState(stored.company);
  const [is_lease, setIsLease] = useState(stored.is_lease);
  const [is_fleet, setIsFleet] = useState(stored.is_fleet);
  const [lessor, setLessor] = useState(stored.lessor);
  const [license, setLicense] = useState(stored.license);
  const [mileage, setMileage] = useState(stored.mileage);
  const [repair_type_id, setRepairTypeId] = useState(stored.car_repair_type_id);
  const [expand, setExpand] = useState(
    !stored.is_lease && !stored.is_fleet && stored.is_company_car !== null,
  );
  const dealer = useSelector((state) => state.dealer);

  const [car, setCar] = useState({});
  const [showKvkDialog, setShowKvkDialog] = useState(false);

  useLayoutEffect(() => {
    navigation?.setOptions({
      headerShown: true,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerTitle: () => (
        <LinearProgress style={{width: 126}} step={1} total={7} />
      ),
    });
  }, [navigation]);

  const reset = () => {
    setCar({});
    setIsLease(false);
    setIsFleet(false);
    setIsCompanyCar(null);
    setExpand(false);
  };

  useEffect(() => {
    const fetch = async () => {
      if (!license || license.length < 6) {
        reset();
        return;
      }

      try {
        const {result: contract} = await carApi.contract(dealer_id, license);
        if (contract?.rob?.isActive) {
          setIsLease(true);
          setLessor(contract.rob.lessor.name);
          setIsFleet(false);
          setIsCompanyCar(null);
          setExpand(false);
        } else if (contract?.fleet?.isActive) {
          setIsFleet(true);
          setLessor(contract.fleet.lessor);
          setIsLease(false);
          setIsCompanyCar(null);
          setExpand(false);
        } else {
          setExpand(true);
        }

        const car = await carApi.fetch(license);
        setCar(car);

        if (car.repair_type_id) {
          setRepairTypeId(car.repair_type_id);
        }
      } catch {
        setCar({});
      }
    };

    fetch();
  }, [license, dispatch]);

  let isValid =
    license &&
    license.length > 5 &&
    (is_lease || is_fleet || is_company_car !== null) &&
    mileage;

  if (isValid && is_company_car) {
    isValid = !!company;
  }

  const onNext = () => {
    if (!isValid) {
      return;
    }

    dispatch(
      addJobSetLicense(license.replace(/[^0-9a-z]/gi, '').toUpperCase()),
    );
    dispatch(addJobSetMileage(mileage));
    dispatch(addJobIsLease(is_lease));
    dispatch(addJobIsFleet(is_fleet));
    dispatch(addJobLessor(lessor));
    dispatch(addJobSetCompanyCar(is_company_car));
    dispatch(addJobSetCompany(company));
    dispatch(addJobSetCarRepairTypeId(repair_type_id));
    setCar({});

    if (is_change && navigation.canGoBack()) {
      navigation.goBack();
    } else if (onDismiss) {
      onDismiss();
    } else {
      navigation.navigate('User', {dealer_id, is_change});
    }
  };

  const content = (
    <>
      {!onDismiss ? (
        <>
          <Divider />
          <View style={[sy['p-4']]}>
            <Text style={sy.xxLarge}>Gegevens van je auto</Text>
            <Text style={[sy.large, sy['mt-4']]}>
              Voor het geven van de juiste mogelijkheden hebben we wat info van
              je auto nodig.
            </Text>
          </View>
          <Divider />
        </>
      ) : (
        <View style={sy.modalHeaderView}>
          <CloseButton
            style={{position: 'absolute', top: 8, left: 8}}
            onPress={onDismiss}
          />
          <Text style={sy.medium}>Auto</Text>
        </View>
      )}
      <View style={sy.contentView}>
        <View style={[sy['p-4'], sy['gap-4']]}>
          <View style={[sy['flex-row'], sy['gap-8'], sy['items-center']]}>
            <DirectionsCarIcon />
            <View style={[sy['flex-1'], sy['gap-2']]}>
              <FormTextInput
                value={license}
                onChangeText={(text) =>
                  setLicense(text?.replace(/[^0-9a-z]/gi, '').toUpperCase())
                }
                label="Kenteken"
                required={true}
                errorMessage={'Kenteken is verplicht'}
                inputProps={{
                  maxLength: 8,
                  autoCapitalize: 'characters',
                }}
              />
              {car?.name && <Text style={{lineHeight: 20}}>{car.name}</Text>}
            </View>
          </View>
          <View style={[sy['flex-row'], sy['gap-8'], sy['items-center']]}>
            <PinIcon />
            <FormNumberInput
              style={[sy['flex-1']]}
              value={mileage}
              onChangeNumber={(number) => setMileage(number)}
              required={true}
              errorMessage={'Kilometerstand is verplicht'}
              digits={0}
              label="Kilometerstand"
              inputProps={{
                maxLength: 7,
              }}
            />
          </View>
        </View>
        <Grouping
          key={expand}
          icon={
            <BusinessIcon
              fill={
                !is_lease && !is_fleet && car?.name && is_company_car === null
                  ? '#FF6C00'
                  : '#4A4A49'
              }
            />
          }
          header={
            is_lease || is_fleet ? (
              lessor
            ) : !car?.name || is_company_car !== null ? (
              <Text style={[sy.medium]}>Eigendom</Text>
            ) : (
              <Text style={[sy.medium, {color: '#FF6C00'}]}>Keuze maken</Text>
            )
          }
          initial={{
            expand,
          }}>
          {!is_lease && !is_fleet && car?.name && (
            <>
              <View style={[sy['gap-4'], sy['py-4']]}>
                <Pressable
                  style={[
                    sy['flex-row'],
                    sy['items-center'],
                    sy['gap-8'],
                    sy['px-4'],
                  ]}
                  onPress={() => setIsCompanyCar(false)}>
                  <RadioButton checked={is_company_car === false} />
                  <Text>Particulier</Text>
                </Pressable>
                <Divider style={[sy['ml-18'], {backgroundColor: '#f2f2f2'}]} />
                <Pressable
                  style={[
                    sy['flex-row'],
                    sy['items-center'],
                    sy['gap-8'],
                    sy['px-4'],
                  ]}
                  onPress={() => setIsCompanyCar(true)}>
                  <RadioButton checked={is_company_car === true} />
                  <Text>Zakelijk</Text>
                </Pressable>
              </View>
              {is_company_car === true && (
                <>
                  <View
                    style={[
                      sy['p-4'],
                      sy['flex-row'],
                      sy['gap-8'],
                      sy['items-center'],
                    ]}>
                    <StoreIcon fill="#4a4a49" />
                    {company?.number ? (
                      <View>
                        <Text>{company.name}</Text>
                        <Link
                          textStyle={[sy.smallRegular, sy['text-lightgray']]}
                          onPress={() => setShowKvkDialog(true)}>
                          KVK-nummer: {company.number}
                        </Link>
                      </View>
                    ) : (
                      <Link onPress={() => setShowKvkDialog(true)}>
                        Bedrijf zoeken (KVK)
                      </Link>
                    )}
                  </View>
                </>
              )}
            </>
          )}
        </Grouping>
        <Divider />
      </View>
    </>
  );

  const footer = (
    <View style={sy.footerView}>
      <PrimaryButton
        disabled={!isValid}
        onPress={onNext}
        icon={
          is_change &&
          (() => <ShoppingCartIcon fill="#ffffff" width={18} height={18} />)
        }>
        {is_change ? 'Controleren' : onDismiss ? 'Opslaan' : 'Volgende'}
      </PrimaryButton>
    </View>
  );

  return (
    <SafeAreaView style={sy.mainView}>
      {dealer && (
        <>
          {onDismiss ? (
            <>
              <ScrollView>{content}</ScrollView>
              <View style={{borderTopWidth: 1, borderTopColor: '#dcdcdc'}}>
                {footer}
              </View>
            </>
          ) : (
            <PublicContentView>
              {{
                content,
                footer,
              }}
            </PublicContentView>
          )}
          <KvkSearchDialog
            visible={showKvkDialog}
            onDismiss={() => setShowKvkDialog(false)}
            onOK={(selected) => {
              setCompany(selected);
              setShowKvkDialog(false);
            }}
          />
        </>
      )}
    </SafeAreaView>
  );
};

export default Index;
